import { useState } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { smsRecords } from "@/mocks/systemModules";

type SmsTab = "records" | "templates" | "query" | "schedule";

const queryTypes = [
  { key: "balance", label: "余额查询", icon: "ri-wallet-3-line", color: "text-[#E8531A]", bg: "bg-orange-50", desc: "用户发送 YECX 查询账户余额" },
  { key: "reading", label: "读数查询", icon: "ri-bar-chart-line", color: "text-teal-600", bg: "bg-teal-50", desc: "用户发送 SDCX 查询最新表计读数" },
  { key: "bill", label: "账单查询", icon: "ri-file-list-3-line", color: "text-green-600", bg: "bg-green-50", desc: "用户发送 ZDCX 查询当月账单" },
  { key: "recharge", label: "充值提醒", icon: "ri-notification-3-line", color: "text-amber-600", bg: "bg-amber-50", desc: "余额低于阈值时自动发送充值提醒" },
];

const smsTemplates = [
  { id: "T001", name: "充值提醒", type: "充值提醒", content: "尊敬的{姓名}，您的燃气账户余额仅剩{余额}元，请及时充值，以免影响正常用气。充值热线：400-000-0000", trigger: "余额低于50元", status: "启用" },
  { id: "T002", name: "余额提醒", type: "余额提醒", content: "【AI智慧燃气】您好，{姓名}，您的账户余额为{余额}元，本月已用气{用气量}m³。如需充值请拨打400-000-0000", trigger: "每月1日", status: "启用" },
  { id: "T003", name: "账单提醒", type: "账单提醒", content: "【AI智慧燃气】{姓名}您好，您{月份}月账单已出，应缴金额{金额}元，请于{截止日期}前完成缴费，逾期将影响正常用气。", trigger: "出账后立即", status: "启用" },
  { id: "T004", name: "安检通知", type: "安检通知", content: "【AI智慧燃气】尊敬的{姓名}，您的燃气安检预约时间为{日期}{时段}，请届时在家配合安检员上门检查。如需改期请拨打400-000-0000", trigger: "安检前1天", status: "启用" },
  { id: "T005", name: "欠费催缴", type: "欠费催缴", content: "【AI智慧燃气】{姓名}您好，您的账户已欠费{金额}元（{月数}个月），请尽快缴费，否则将于{停气日期}停止供气。缴费热线：400-000-0000", trigger: "欠费超30天", status: "启用" },
  { id: "T006", name: "读数通知", type: "读数查询", content: "【AI智慧燃气】您好，您的燃气表当前读数为{读数}m³，本月用气量{用气量}m³，账户余额{余额}元。", trigger: "用户查询时", status: "启用" },
];

const scheduleList = [
  { id: "SCH-001", name: "月度账单提醒", template: "账单提醒", target: "全部用户", schedule: "每月1日 09:00", nextRun: "2026-05-01 09:00", status: "启用", count: 12456 },
  { id: "SCH-002", name: "低余额充值提醒", template: "充值提醒", target: "余额<50元用户", schedule: "每日 10:00", nextRun: "2026-04-11 10:00", status: "启用", count: 234 },
  { id: "SCH-003", name: "欠费催缴", template: "欠费催缴", target: "欠费用户", schedule: "每周一 09:00", nextRun: "2026-04-13 09:00", status: "启用", count: 89 },
  { id: "SCH-004", name: "安检前日提醒", template: "安检通知", target: "次日安检用户", schedule: "每日 18:00", nextRun: "2026-04-10 18:00", status: "启用", count: 15 },
];

export default function SmsPage() {
  const [activeTab, setActiveTab] = useState<SmsTab>("records");
  const [composeModal, setComposeModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendDone, setSendDone] = useState(false);
  const [preview, setPreview] = useState<typeof smsRecords[0] | null>(null);
  const [queryResult, setQueryResult] = useState<{ type: string; result: string } | null>(null);
  const [queryPhone, setQueryPhone] = useState("");
  const [queryType, setQueryType] = useState("balance");

  const stats = {
    total: smsRecords.reduce((s, r) => s + r.count, 0),
    sent: smsRecords.reduce((s, r) => s + r.sent, 0),
    failed: smsRecords.reduce((s, r) => s + r.failed, 0),
    pending: smsRecords.filter(r => r.status === "待发送").reduce((s, r) => s + r.count, 0),
  };

  const handleSend = () => {
    setSending(true);
    setTimeout(() => { setSending(false); setSendDone(true); }, 2000);
  };

  const handleQuery = () => {
    const results: Record<string, string> = {
      balance: "用户 张建国（R001）账户余额：¥128.50，本月已用气 256.8m³",
      reading: "用户 张建国（R001）当前表计读数：2,456.8m³，本月用气量：24.1m³",
      bill: "用户 张建国（R001）2026年4月账单：¥89.34，截止日期：2026-04-30",
      recharge: "已向 张建国（R001）发送充值提醒短信，余额 ¥128.50 > 50元，无需发送",
    };
    setQueryResult({ type: queryType, result: results[queryType] || "查询成功" });
  };

  const typeColors: Record<string, string> = {
    "欠费催缴": "bg-red-50 text-red-600",
    "安检通知": "bg-amber-50 text-amber-600",
    "报装进度": "bg-teal-50 text-teal-600",
    "节日提醒": "bg-green-50 text-green-600",
  };

  const tabs = [
    { key: "records", label: "发送记录", icon: "ri-message-2-line" },
    { key: "templates", label: "短信模板", icon: "ri-file-text-line" },
    { key: "query", label: "查询功能", icon: "ri-search-line" },
    { key: "schedule", label: "定时任务", icon: "ri-time-line" },
  ];

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">短信服务中心</h1>
            <p className="text-sm text-gray-500 mt-0.5">充值提醒、余额提醒、账单提醒、余额查询、读数查询等短信功能</p>
          </div>
          <button onClick={() => { setComposeModal(true); setSendDone(false); }} className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d44a15] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-send-plane-line"></i>发送短信
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "发送总量", value: stats.total.toLocaleString(), color: "text-gray-800", bg: "bg-gray-50", icon: "ri-message-2-line" },
            { label: "发送成功", value: stats.sent.toLocaleString(), color: "text-green-600", bg: "bg-green-50", icon: "ri-check-line" },
            { label: "发送失败", value: stats.failed.toLocaleString(), color: "text-red-600", bg: "bg-red-50", icon: "ri-close-line" },
            { label: "待发送", value: stats.pending.toLocaleString(), color: "text-amber-600", bg: "bg-amber-50", icon: "ri-time-line" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white">
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <div>
                <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key as SmsTab)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <i className={t.icon}></i>{t.label}
            </button>
          ))}
        </div>

        {/* Records tab */}
        {activeTab === "records" && (
          <div className="space-y-3">
            {smsRecords.map(record => (
              <div key={record.id} className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                      <i className="ri-message-2-line text-[#E8531A] text-lg"></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">{record.type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[record.type] || "bg-gray-100 text-gray-600"}`}>{record.type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${record.status === "已完成" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{record.status}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">目标: {record.target} · 发送时间: {record.sendTime}</div>
                    </div>
                  </div>
                  <button onClick={() => setPreview(record)} className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-3 py-1 rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap">查看模板</button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>发送进度</span>
                      <span>{record.count > 0 ? Math.round((record.sent / record.count) * 100) : 0}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-[#E8531A] h-2 rounded-full" style={{ width: `${record.count > 0 ? (record.sent / record.count) * 100 : 0}%` }} />
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs">
                    <div className="text-center"><div className="font-bold text-gray-800">{record.count.toLocaleString()}</div><div className="text-gray-400">总数</div></div>
                    <div className="text-center"><div className="font-bold text-green-600">{record.sent.toLocaleString()}</div><div className="text-gray-400">成功</div></div>
                    <div className="text-center"><div className="font-bold text-red-500">{record.failed}</div><div className="text-gray-400">失败</div></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Templates tab */}
        {activeTab === "templates" && (
          <div className="space-y-3">
            {smsTemplates.map(tpl => (
              <div key={tpl.id} className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900">{tpl.name}</span>
                      <span className="text-xs bg-orange-50 text-[#E8531A] px-2 py-0.5 rounded-full">{tpl.type}</span>
                      <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">{tpl.status}</span>
                    </div>
                    <div className="text-xs text-gray-400">触发条件: {tpl.trigger}</div>
                  </div>
                  <div className="flex gap-2">
                    <button className="text-xs text-gray-500 border border-gray-200 px-3 py-1 rounded-lg cursor-pointer hover:bg-gray-50 whitespace-nowrap">编辑</button>
                    <button className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-3 py-1 rounded-lg cursor-pointer hover:bg-orange-50 whitespace-nowrap">测试发送</button>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-600 leading-relaxed">{tpl.content}</div>
              </div>
            ))}
          </div>
        )}

        {/* Query tab */}
        {activeTab === "query" && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {queryTypes.map(qt => (
                <div key={qt.key} className={`${qt.bg} rounded-xl p-4`}>
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white mb-3">
                    <i className={`${qt.icon} ${qt.color} text-lg`}></i>
                  </div>
                  <div className={`text-sm font-semibold ${qt.color} mb-1`}>{qt.label}</div>
                  <div className="text-xs text-gray-500">{qt.desc}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">手动查询测试</h3>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">查询类型</label>
                  <select value={queryType} onChange={e => setQueryType(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                    <option value="balance">余额查询</option>
                    <option value="reading">读数查询</option>
                    <option value="bill">账单查询</option>
                    <option value="recharge">充值提醒</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">用户手机号</label>
                  <input type="tel" placeholder="输入手机号" value={queryPhone} onChange={e => setQueryPhone(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
                <div className="flex items-end">
                  <button onClick={handleQuery} className="w-full bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">查询</button>
                </div>
              </div>
              {queryResult && (
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-checkbox-circle-line text-green-500 text-sm"></i>
                    <span className="text-xs font-semibold text-green-700">查询结果</span>
                  </div>
                  <p className="text-sm text-gray-700">{queryResult.result}</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">短信指令说明</h3>
              <div className="space-y-2">
                {[
                  { cmd: "YECX", desc: "余额查询 - 回复当前账户余额及本月用气量" },
                  { cmd: "SDCX", desc: "读数查询 - 回复最新表计读数及本月用气量" },
                  { cmd: "ZDCX", desc: "账单查询 - 回复当月账单金额及截止日期" },
                  { cmd: "CZTX", desc: "充值提醒 - 主动触发充值提醒短信" },
                  { cmd: "TDCX", desc: "停订查询 - 查询是否已停气及原因" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 py-2 border-b border-gray-50 last:border-0">
                    <span className="text-xs font-mono font-bold text-[#E8531A] bg-orange-50 px-2 py-1 rounded-lg w-16 text-center">{item.cmd}</span>
                    <span className="text-xs text-gray-600">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Schedule tab */}
        {activeTab === "schedule" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">定时发送任务</span>
              <button className="flex items-center gap-1.5 text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                <i className="ri-add-line"></i>新建任务
              </button>
            </div>
            {scheduleList.map(sch => (
              <div key={sch.id} className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900">{sch.name}</span>
                      <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">{sch.status}</span>
                    </div>
                    <div className="text-xs text-gray-400">模板: {sch.template} · 目标: {sch.target}</div>
                  </div>
                  <div className="flex gap-2">
                    <button className="text-xs text-gray-500 border border-gray-200 px-3 py-1 rounded-lg cursor-pointer hover:bg-gray-50 whitespace-nowrap">暂停</button>
                    <button className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-3 py-1 rounded-lg cursor-pointer hover:bg-orange-50 whitespace-nowrap">立即执行</button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <div className="text-gray-400 mb-0.5">执行计划</div>
                    <div className="font-medium text-gray-700">{sch.schedule}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <div className="text-gray-400 mb-0.5">下次执行</div>
                    <div className="font-medium text-gray-700">{sch.nextRun}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <div className="text-gray-400 mb-0.5">目标用户数</div>
                    <div className="font-medium text-[#E8531A]">{sch.count.toLocaleString()} 人</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {composeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !sending && setComposeModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[500px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">发送短信</h3>
            {!sendDone ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">短信类型</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                    <option>充值提醒</option>
                    <option>余额提醒</option>
                    <option>账单提醒</option>
                    <option>欠费催缴</option>
                    <option>安检通知</option>
                    <option>自定义</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">发送对象</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                    <option>全部用户</option>
                    <option>低余额用户（余额&lt;50元）</option>
                    <option>欠费用户</option>
                    <option>待安检用户</option>
                    <option>指定用户</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">短信内容</label>
                  <textarea rows={4} maxLength={500} defaultValue="尊敬的{姓名}，您的燃气余额仅剩{余额}元，请及时充值，以免影响正常用气。如需帮助请拨打服务热线：400-xxx-xxxx" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A] resize-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">发送时间</label>
                  <div className="flex gap-2">
                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="sendTime" defaultChecked className="accent-[#E8531A]" /><span className="text-sm text-gray-700">立即发送</span></label>
                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="sendTime" className="accent-[#E8531A]" /><span className="text-sm text-gray-700">定时发送</span></label>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setComposeModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
                  <button onClick={handleSend} disabled={sending} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap">
                    {sending ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>发送中...</span> : "确认发送"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-green-500 text-3xl"></i>
                </div>
                <div className="text-base font-bold text-gray-900 mb-1">发送成功！</div>
                <div className="text-sm text-gray-500 mb-4">短信已成功加入发送队列</div>
                <button onClick={() => setComposeModal(false)} className="bg-[#E8531A] text-white px-6 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap">关闭</button>
              </div>
            )}
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl p-6 w-[400px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">短信模板预览</h3>
            <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed">{preview.template}</div>
            <button onClick={() => setPreview(null)} className="w-full mt-4 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">关闭</button>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
